import { Link } from 'react-router-dom';
import { useLicense, RESTRICTIVE_STATUSES, WARNING_STATUSES } from '../../lib/license.jsx';

// Per-status copy. Keep messages short and actionable — the user is
// looking at this from above the page, not from a dedicated docs view.
// Tone derived from the status family in classify().
const COPY = {
  not_activated:             { title: 'License not activated',           hint: 'Open Settings → KMS License and run renewal, or contact the administrator.' },
  ipc_unreachable:           { title: 'Cannot reach licensing service',  hint: 'ad-kms-service is not responding. Some actions are blocked. systemctl status ad-kms-service on the VM.' },
  expired:                   { title: 'License expired',                 hint: 'Renew the license in Settings → KMS License or contact the administrator.' },
  revoked:                   { title: 'License revoked',                 hint: 'Contact the administrator — this installation is no longer authorised.' },
  hardware_mismatch:         { title: 'Hardware fingerprint changed',    hint: 'Hardware no longer matches the registered slot. Administrator must reset the slot in KMS, then click Renew.' },
  software_uuid_mismatch:    { title: 'Software UUID does not match',    hint: 'LICENSE_UUID in /opt/adms/ad-kms-service/.env points to the wrong product.' },
  signature_invalid:         { title: 'License signature invalid',       hint: 'Token is signed by a key not embedded in this build of ad-kms-service. Microservice update required.' },
  tampering_detected:        { title: 'Microservice integrity violated', hint: 'Self-check failed — the ad-kms-service binary or its manifest has been tampered with. Reinstall the .deb.' },
  unsupported_token_version: { title: 'Unsupported license token',       hint: 'Microservice is older than the issued token. Update ad-kms-service.' },
  unsupported_crypto_suite:  { title: 'Unsupported crypto suite',        hint: 'Microservice is older than the issued token. Update ad-kms-service.' },
  unknown:                   { title: 'License status not yet known',    hint: 'Initial poll has not completed. If this persists, ad-kms-service may not be running.' },

  // warning tier
  active_with_warning:       { title: 'License has warnings',             hint: 'Open Settings → KMS License to review the warnings list.' },
  time_unsynced:             { title: 'System time is not synchronised',  hint: 'chrony is not tracking the upstream NTP source. Run chronyc tracking on the VM to diagnose.' }
};

// Tones — exhaustive Tailwind classes so the JIT picks them up.
const TONES = {
  red: {
    bg: 'bg-red-100',
    border: 'border-red-200',
    text: 'text-red-900',
    icon: 'text-red-600',
    link: 'text-red-700 hover:text-red-900'
  },
  amber: {
    bg: 'bg-amber-100',
    border: 'border-amber-200',
    text: 'text-amber-900',
    icon: 'text-amber-600',
    link: 'text-amber-700 hover:text-amber-900'
  }
};

function classify(snapshot) {
  if (!snapshot) return null;
  const { status, days_until_expiry: dE, days_until_maintenance: dM } = snapshot;

  // Restrictive — red, blocking
  if (RESTRICTIVE_STATUSES.has(status)) {
    const c = COPY[status] ?? { title: `License status: ${status}`, hint: 'Open Settings → KMS License.' };
    return { tone: 'red', icon: 'fa-shield-xmark', ...c };
  }

  // Active but warning tier — amber, non-blocking
  if (WARNING_STATUSES.has(status)) {
    const c = COPY[status] ?? { title: `License status: ${status}`, hint: 'Open Settings → KMS License.' };
    return { tone: 'amber', icon: 'fa-triangle-exclamation', ...c };
  }

  // Active + days warnings (TZ §9.2)
  if (typeof dE === 'number' && dE >= 0 && dE < 30) {
    return {
      tone: 'red',
      icon: 'fa-hourglass-end',
      title: `License expires in ${dE} ${dE === 1 ? 'day' : 'days'}`,
      hint: 'Renew the license soon — afterwards all admin actions and tile traffic will be blocked.'
    };
  }
  if (typeof dM === 'number' && dM >= 0 && dM < 30) {
    return {
      tone: 'amber',
      icon: 'fa-stopwatch',
      title: `Support window ends in ${dM} ${dM === 1 ? 'day' : 'days'}`,
      hint: 'Auto-renewal should kick in soon. If it doesn’t, click Renew in Settings → KMS License.'
    };
  }

  return null;
}

export default function LicenseBanner() {
  const { snapshot } = useLicense();
  const c = classify(snapshot);
  if (!c) return null;
  const t = TONES[c.tone];

  return (
    <div
      role="alert"
      className={`flex items-start gap-3 border-b ${t.border} ${t.bg} px-6 py-2.5 text-sm ${t.text}`}
    >
      <i className={`fa-solid ${c.icon} mt-0.5 ${t.icon}`} />
      <div className="flex-1 min-w-0">
        <span className="font-semibold">{c.title}</span>
        <span className="ml-2 opacity-90">{c.hint}</span>
      </div>
      <Link
        to="/settings"
        className={`shrink-0 font-semibold underline-offset-2 hover:underline ${t.link}`}
      >
        Open Settings <i className="fa-solid fa-arrow-right text-xs" />
      </Link>
    </div>
  );
}
